import { useCallback, useEffect, useState } from 'react';
import {
  DateTimeRangeFilter,
  DESC,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  SearchInput,
} from 'framework/list';
import useApplicationService, {
  APPLICATIONS_STAGES_ORDER,
} from 'domain/recruiting/applications/shared/applicationService';
import { Box } from '@mui/material';
import ApplicationsDataGrid from 'domain/recruiting/applications/List/ApplicationsDataGrid';
import SearchVacancyFilter from 'domain/recruiting/vacancy/List/filter/SearchVacancyFilter';
import ApplicationStatusFilter from 'domain/recruiting/candidate/applications/shared/filter/ApplicationStatusFilter';
import GroupedApplicationsList from './GroupedApplicationList';
import ApplicationsGroupButton from 'domain/recruiting/applications/shared/button/ApplicationGroupButton';
import useApplicationsSettings from './GroupedApplicationList/Settings/useApplicationsSettings';
import { EmployeeFilter } from 'domain/shared/employee';
import { CollapseControls } from 'framework/ui/mui-builder';

const filters = (
  <Filters>
    <FiltersLayout>
      <SearchVacancyFilter label="Vacancy" source="vacancy.alias" />
      <ApplicationStatusFilter label="Status" source="status" />
      <DateTimeRangeFilter source="createdAt" label="Application creation date" />
      <EmployeeFilter source="createdBy.alias" label="Author" />
    </FiltersLayout>
  </Filters>
);

const ApplicationsList = () => {
  const { searchWithFormattedFilters } = useApplicationService();
  const [isGrouped, setIsGrouped] = useState(true);
  const { collapseAll, expandAll, areAllItemsCollapsed, areAllItemsExpanded } = useApplicationsSettings();

  const collapseControls = (
    <CollapseControls
      isExpandButtonShown={!areAllItemsExpanded(APPLICATIONS_STAGES_ORDER)}
      isCollapseButtonShown={!areAllItemsCollapsed()}
      expandAll={() => expandAll(APPLICATIONS_STAGES_ORDER)}
      collapseAll={collapseAll}
    />
  );

  const onClickGroupButton = useCallback(() => {
    setIsGrouped(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isGrouped) {
      collapseAll();
    }
    // Suppressed warnings because we only need to call useEffect callback only after updating isGrouped
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrouped]);

  const header = (
    <Box sx={{ display: { xs: 'block', sm: 'flex' }, alignItems: 'center', justifyContent: 'space-between' }}>
      <ListHeader label="Applications" />
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: { xs: '12px', sm: 0 } }}>
        <ApplicationsGroupButton isActive={isGrouped} onClick={onClickGroupButton} />
        {isGrouped ? collapseControls : null}
      </Box>
    </Box>
  );

  return (
    <List
      key={`applications-list-${isGrouped}`}
      getDataMethod={params => (isGrouped ? Promise.resolve({ result: [] }) : searchWithFormattedFilters(params))}
      defaultSortOrder={DESC}
      defaultSortField="createdAt"
    >
      <ListPageLayout header={header} filter={filters} search={<SearchInput label="ID, сandidate's name" />}>
        {isGrouped ? (
          <>
            <Box mb={1.5}>
              {APPLICATIONS_STAGES_ORDER.map((stage, index) => {
                return <GroupedApplicationsList key={`applications-${stage}`} stage={stage} index={index} />;
              })}
            </Box>

            <Box
              className="collapse-controls__wrapper"
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                mb: 2.5,
                justifyContent: { md: 'flex-end' },
              }}
            >
              {collapseControls}
            </Box>
          </>
        ) : (
          <ApplicationsDataGrid />
        )}
      </ListPageLayout>
    </List>
  );
};

export default ApplicationsList;
